import {
  Box,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  useTheme,
  Grid, TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { imageOnError } from '../helpers';
import { PAGES_SLUGS, THEME_COLORS } from '../variables';
import { MainContainer } from '../styles';
import { useState } from 'react';
import { sendBudgetRequestPrivateLabel } from '../api';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { BsCheckCircle } from 'react-icons/bs';
import { IconContext } from 'react-icons';
import UploadFileCard from './UploadCard';
import QuantityPrivateLabel from '../components/Quantity/QuantityPrivateLabel';


const privateLabelResume = ({ products }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [notes, setNotes] = useState('');
  const [sizes, setSizes] = useState([
    { name: 'XS', quantity: 0 },
    { name: 'S', quantity: 0 },
    { name: 'M', quantity: 0 },
    { name: 'L', quantity: 0 },
    { name: 'XL', quantity: 0 },
    { name: '2XL', quantity: 0 },
    { name: '3XL', quantity: 0 },
    { name: '4XL', quantity: 0 },
    { name: '5XL', quantity: 0 }
  ]);

  const handleSizeQuantityChange = (index, value) => {
    const newSizes = [...sizes];
    const numeric = parseInt(value, 10);
    newSizes[index].quantity = isNaN(numeric) ? 0 : Math.max(0, numeric);
    setSizes(newSizes);
  };


  const handleOBS = (e) => {
    setNotes(e.target.value);
  };

  function handleConfirmOrder(e) {
    e.preventDefault();

    setIsLoading(true);
    const files = products
      .filter(p => p?.file instanceof File)
      .map(p => p.file);
    const formData = new FormData();
    formData.append('body', JSON.stringify({
      products: products,
      quantities: sizes,
      notes: notes
    }));

    files.forEach((file) => {
      formData.append('files', file); // todos com a mesma key
    });
    sendBudgetRequestPrivateLabel(formData)
      .then(() => {
        setIsLoading(false);
        setShowConfirmation(true);
      })
      .catch(
        ({
           response: {
             status
           }
         }) => {
          if (status === 401) {
            navigate(PAGES_SLUGS.login);
          } else if (status === 400 || status > 401) {
            setIsLoading(false);
          }
        }
      );
  }

  const totalSizeQuantity = sizes.reduce((acc, s) => acc + s.quantity, 0);


  if (showConfirmation) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          marginTop: { xs: 3, md: 4 }
        }}
      >
        <IconContext.Provider
          value={{
            style: { color: THEME_COLORS.lightGreen }
          }}
        >
          <BsCheckCircle size={50} />
        </IconContext.Provider>
        <Typography
          sx={{
            marginTop: 2,
            fontWeight: 600
          }}
        >
          {t('budgetRequest.successMessage')}
        </Typography>
      </Box>
    );
  }

  return (
    <MainContainer>
      <Grid container spacing={2}>
        {/* 🟢 Lado Esquerdo */}
        <Grid item xs={12} md={6}>
          <Box sx={{
            ml: 'auto',
            p: 2,
            borderRadius: 2
          }}>
            <Typography variant="h6" gutterBottom align="left" fontWeight={'bold'}>
              Finalizar Personalização
            </Typography>
            <Box>
              <Typography
                sx={{
                  marginTop: 3,
                  marginBottom: 2,
                  fontWeight: 600
                }}
              >1. Adicionar Etiqueta Personalizada (Opcional)
              </Typography>
              <UploadFileCard onFileSelect={(file) => {
                products.push({
                  name: 'Etiqueta',
                  title: file.name,
                  type: file.type,
                  file: file
                });
              }} />
            </Box>
            <Typography
              sx={{
                marginTop: 4,
                fontWeight: 600,
                marginBottom: 2
              }}
            >
              2. Selecione as quantidades estimadas
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              columnGap: '10px',
              padding: '8px 0',
              marginBottom: '1',
              height: '30px'
            }}>
              {sizes.map((variant, vIndex) => (
                <QuantityPrivateLabel
                  key={`sizeVariant-${vIndex}`}
                  product={variant}
                  isProductVariant
                  variant="size"
                  label={variant.name}
                  onQuantityChange={(value) => handleSizeQuantityChange(vIndex, value)}
                />
              ))}

            </Box>
            <Box>
              <Typography sx={{
                marginTop: 2,
                fontWeight: 700,
                marginBottom: 2
              }}>Quantidades Selecionadas: {totalSizeQuantity}</Typography>
            </Box>
            <Typography
              sx={{
                marginTop: 4,
                fontWeight: 600
              }}
            >
              3. Observações
            </Typography>
            <TextField
              sx={{ marginTop: '16px' }}
              id="notes"
              label={'Informações adicionais ao seu pedido'}
              multiline
              rows={3}
              fullWidth
              onChange={handleOBS}
              value={notes}
            />

          </Box>
        </Grid>

        {/* 🔵 Lado Direito - Resumo */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              ml: 'auto', // Isto empurra o resumo para o canto direito
              p: 2,
              borderRadius: 2
            }}
          >
            <Typography variant="h6" gutterBottom align="left" fontWeight={'bold'}>
              Resumo
            </Typography>

            <Table>
              <TableBody>
                {products.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        borderBottom: `1px solid ${THEME_COLORS.gray}`,
                        fontWeight: 'bold',
                        textAlign: 'left !important',
                        padding: '0'
                      }}
                    >
                      {products[3].title.includes('curta') && index > 3 ?
                        t(`privatelabel.steps.${index + 2}`).includes('Finalize') ? 'Etiqueta' : t(`privatelabel.steps.${index + 2}`)
                        : t(`privatelabel.steps.${index + 1}`).includes('Finalize') ? 'Etiqueta' : t(`privatelabel.steps.${index + 1}`)}
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'right !important',
                      padding: '0',
                      display: 'flex',
                      justifyContent: 'end',
                      alignItems: 'center'
                    }}>
                      {item.title}
                      <Box
                        component="img"
                        src={item.images ? item.images[0] : ''}
                        alt={item.images ? item.reference : ''}
                        onError={(e) => {
                          imageOnError(e);
                        }}
                        sx={{
                          aspectRatio: '1',
                          objectFit: 'contain',
                          width: '38px',
                          backgroundColor: 'white',
                          borderRadius: theme.shape.borderRadius / 10,
                          border: `1px solid ${THEME_COLORS.gray}`,
                          ml: 1,
                          margin: '6px 0 6px 4px'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>


            <Typography variant="caption" display="block" mt={2} align="right">
              {t('privatelabel.resume.warning')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Box
        component="form"
        onSubmit={handleConfirmOrder}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 3,
            marginTop: 3
          }}
        >
          <LoadingButton
            variant="contained"
            disabled={totalSizeQuantity === 0 || isLoading}
            type="submit"
            loading={isLoading}
          >
            {t('forms.submitRequest')}
          </LoadingButton>

        </Box>
      </Box>
    </MainContainer>
  );
};

export default privateLabelResume;
